import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import * as yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import WarningIconSm from 'components/Icons/WarningIconSm';
import SubmitButton from 'components/SubmitButton';
import patterns from 'helpers/patterns';
import {
  AppleAuthButton,
  GoogleAuthButton,
} from 'modules/corporateOffer/components/authButtons';
import { ContinueWith } from 'modules/corporateOffer/components/authButtons/ContinueWith';
import {
  useSignInAndApplyOffer,
  useSocialSignInAndApplyOffer,
} from 'modules/corporateOffer/hooks/useSignInAndApplyOffer';
import theme from 'theme/theme';

import Input from '../inputs/Input';
import PasscodeInput from '../inputs/PasscodeInput';
import { ErrorText } from '../inputs/StyledFormInputs';

import { FIELDS, TEMPLATE_INPUT_ID } from './constants';
import { OnboardingSubtitle, OnboardingTitle } from './StyledComponents';
import { EMAIL_INPUT_ID, emailValidationSchema } from './validationSchemas';

const validationSchema = yup
  .object({
    [TEMPLATE_INPUT_ID]: yup
      .string()
      .length(FIELDS.length, 'Invalid passcode')
      .matches(patterns.passcodePattern, {
        message: 'Only numbers allowed',
        excludeEmptyString: true,
      })
      .required('This field is required'),
  })
  .concat(emailValidationSchema);

type SignInData = yup.InferType<typeof validationSchema>;

type Props = {
  account: string;
  onForgotPassword: () => void;
};

const SignInStep: FC<Props> = ({ account, onForgotPassword }) => {
  const [authError, setAuthError] = useState('');

  const {
    handleSubmit,
    formState: { errors },
    control,
    register,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      EMAIL_INPUT_ID: '',
      TEMPLATE_INPUT_ID: '',
    },
  });

  const {
    mutate: mutateEmail,
    isLoading: isLoadingEmail,
    isError: isErrorEmail,
  } = useSignInAndApplyOffer(account);
  const {
    mutate: mutateSocial,
    isLoading: isLoadingSocial,
    isError: isErrorSocial,
  } = useSocialSignInAndApplyOffer(account);
  const isError = isErrorEmail || isErrorSocial;
  const isLoading = isLoadingEmail || isLoadingSocial;

  const onSubmit = async (data: SignInData) => {
    mutateEmail({
      email: data.EMAIL_INPUT_ID,
      password: data.TEMPLATE_INPUT_ID,
    });
  };

  const handleForgotPassword = () => {
    onForgotPassword();
  };

  return (
    <Container>
      <header>
        <OnboardingTitle
          title={isError ? 'Invalid email and/or passcode' : 'Sign in'}
          error={isError}
        />
        <OnboardingSubtitle
          subtitle={`Please ${
            isError ? 'confirm' : 'enter'
          } your email & passcode`}
          error={isError}
        />
      </header>
      <SignInForm onSubmit={handleSubmit(onSubmit)}>
        <Input<SignInData>
          label="Email"
          inputId={EMAIL_INPUT_ID}
          inputType="email"
          control={control}
          register={register}
          error={errors.EMAIL_INPUT_ID}
        />
        <PasscodeInput<SignInData>
          label="Passcode"
          inputId={TEMPLATE_INPUT_ID}
          control={control}
          register={register}
          error={errors.TEMPLATE_INPUT_ID}
        />
        {!!authError && (
          <ErrorText $error={!!authError}>
            <WarningIconSm color={theme.colors.primary} />
            {authError}
          </ErrorText>
        )}
        <ButtonWrapper data-testid="submit-button-wrapper">
          <SubmitButton
            text="SIGN IN"
            isDisabled={Object.keys(errors).length > 0 || isLoading}
            borderSize="xl"
            buttonSize="xl"
            fontSize="md"
          />
        </ButtonWrapper>
      </SignInForm>
      <LinkContainer>
        <PasscodeRecoveryLink onClick={handleForgotPassword}>
          Forgot passcode?
        </PasscodeRecoveryLink>
      </LinkContainer>
      <ContinueWith />
      <SocialButtonsWrapper>
        <AppleAuthButton
          disabled={isLoading}
          onSuccess={(data) => {
            mutateSocial({ token: data.token });
          }}
          onError={setAuthError}
        />
        <GoogleAuthButton
          disabled={isLoading}
          onSuccess={(data) => {
            mutateSocial({ token: data.token });
          }}
          onError={setAuthError}
        />
      </SocialButtonsWrapper>
      <NoteText>
        This page is only for managing your account details.{'\n'}To use Shido,
        download the app to your phone and sign in there.
      </NoteText>
    </Container>
  );
};

const Container = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const SignInForm = styled.form`
  margin: 70px auto 0;
  max-width: 600px;
  padding-left: 20px;
  padding-right: 20px;
  width: 100%;

  ${({ theme }) => theme.breakpoints.down('md')} {
    max-width: 335px;
    margin: 33px auto 0;
    padding-left: 0;
    padding-right: 0;
  }
`;

const ButtonWrapper = styled.div`
  margin: 38px auto 0;
  display: flex;
  justify-content: center;
`;

export const SocialButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  margin-top: 33px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    gap: 20px;
    align-items: stretch;
  }
`;

export const PasscodeRecoveryLink = styled.div`
  font-size: 16px;
  font-weight: 700;
  text-align: center;
  display: block;
  cursor: pointer;
  margin-top: 40px;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.darkBlueV2};

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-size: 14px;
    margin-top: 17px;
  }
`;

export const LinkContainer = styled.div`
  display: grid;
  justify-content: center;
`;

export const NoteText = styled.p`
  color: ${({ theme }) => theme.colors.darkBlue};
  font-size: 16px;
  font-weight: 300;
  line-height: 30px;
  text-align: center;
  white-space: pre-line;
  margin-top: 76px;
  padding-bottom: 40px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-size: 12px;
    max-width: 327px;
    white-space: unset;
    line-height: 20px;
    margin: 35px auto 0;
    font-weight: 300;
    padding-bottom: 20px;
  }
`;

export default SignInStep;
